import { ActionIcon, AppShell, CloseButton, Drawer, type FloatingPosition, Menu, ScrollArea, Tooltip, UnstyledButton } from '@mantine/core'
import { useDisclosure, useHotkeys, useLocalStorage, useMediaQuery, useOs } from '@mantine/hooks'
import {
  IconChevronRight,
  IconLayoutGrid,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
  IconMenu2,
  IconPlus,
  IconSearch,
} from '@tabler/icons-react'
import type { Tone } from '@tnn/core'
import { type ComponentType, type CSSProperties, type ReactNode, type UIEvent, useLayoutEffect, useRef, useState } from 'react'
import { toneVars } from '../components/display'
import { useLinkComponent } from '../link'
import classes from './Shell.module.css'

export type IconComponent = ComponentType<{ size?: number | string; stroke?: number; className?: string }>

/** One kind of thing waiting on a menu item (to approve, to pay) */
export interface NavCountPart {
  count: number
  /** What they are, shown on hovering the number */
  label: string
  /** The status's colour in the list it opens (none: `countTone`) */
  tone?: Tone
}

export interface NavEntry {
  key: string
  label: string
  icon: IconComponent
  href: string
  active?: boolean
  count?: number | string
  /** The number's colour (default `warning`: something waits) */
  countTone?: Tone
  /** `count` apart by kind, a pill each; the rail and the bottom bar show only their sum, in the first one's colour */
  parts?: NavCountPart[]
  /** Shown in the phone's bottom navigation bar (keep it to 3–5 items) */
  bottom?: boolean
  /**
   * Other paths whose pages are its own (Thananan OS's หน้าแรก: `/h`): it is in front on the top or bottom bar there, beside
   * the menu item of the page itself
   */
  owns?: string[]
  /** Picture drawn instead of the icon at the nav's size, used for links to other apps */
  mark?: (size: number) => ReactNode
  /** Another app: a plain link (`href` a full address), so the page loads that app in place of this one */
  external?: boolean
}

export interface NavSection {
  key: string
  label?: string
  items: NavEntry[]
}

export interface ShellFabItem {
  key: string
  label: string
  icon: IconComponent
  href: string
}

export interface ShellFab {
  label: string
  icon: IconComponent
  href?: string
  onClick?: () => void
  /** Several things to make: the button opens a menu of them */
  items?: ShellFabItem[]
  /** false: not floating on a phone, where it would cover the page's own buttons (on a page it opens) */
  floats?: boolean
}

export interface AsidePanel {
  key: string
  label: string
  icon: IconComponent
  badge?: number
  content: ReactNode
}

export interface ShellLabels {
  menu: string
  expand: string
  collapse: string
  close: string
  /** The sheet of apps to change to (`osShell`) */
  switchApp?: string
  /** A phone's bottom bar's button to the apps (`osShell`) */
  apps?: string
}

export interface ShellProps {
  appName: string
  /** The app's icon in the top bar, 36px */
  appMark: ReactNode
  homeHref?: string
  nav: NavSection[]
  fab?: ShellFab
  searchPlaceholder: string
  onSearch: () => void
  /** Icons at the right of the top bar on tablet and desktop */
  actions?: ReactNode
  /** Avatar button with its menu */
  account: ReactNode
  asidePanels?: AsidePanel[]
  /** Small chip next to the app name, e.g. "demo data" */
  badge?: ReactNode
  /**
   * The page shown (the router location's key). A phone's page scrolls in its own box, not the window, so the shell
   * keeps where each was: a new page starts at the top, one gone back to is where it was left
   */
  scrollKey?: string
  /**
   * The page area holds a stack of pages that each scroll in their own box (`StackOutlet` in @tnn/app-kit): the shell
   * leaves their scrolling to them and only watches it, to shrink the floating create button
   */
  stacked?: boolean
  /** A page that is the whole of a phone's screen (a chat room, its own header and box to type in): no search bar or bottom bar */
  bare?: boolean
  /**
   * Thananan OS's own frame, with its apps in it. A computer: a top bar of the bottom bar's items, the apps (the marks in
   * `nav`), search and the account; under it the app in front's menu (`side`, which folds to icons) and its pages. A
   * phone: the bottom bar always, ending in แอพ (to the app last in front; pressed again there, the apps to change to),
   * and at the top where one is, an app's name with ☰ opening its menu as a sheet
   */
  osShell?: boolean
  /** With `osShell`, the app last in front (its item's `key` in `nav`), where a phone's แอพ goes back to */
  lastApp?: string
  /**
   * With `osShell`, the page to ask for another app: a + after a computer's app tabs, at the end of a phone's sheet of apps
   * (and its แอพ, with none yet); `active` on that page, which marks the + (a phone's แอพ)
   */
  askApps?: { label: string; href: string; active?: boolean }
  /** With `osShell`, a page that fills its area edge to edge (chat) rather than sitting on a sheet */
  flush?: boolean
  /** With `osShell`, the app in front and its own menu: its name and mark, its create button, its items */
  side?: { title: string; mark: ReactNode; nav: NavSection[]; fab?: ShellFab; footer?: ReactNode }
  labels: ShellLabels
  children: ReactNode
}

const NAV_WIDTH = 256
const RAIL_WIDTH = 88
const PANEL_WIDTH = 352

/** How far down each page visited on a phone was scrolled, by `scrollKey` */
const scrolledTo = new Map<string, number>()

/**
 * The frame every app shares. Desktop: top bar, navigation drawer with a
 * create button, content sheet and a rail of side panels. Tablet: the drawer
 * becomes a rail. Phone: pill search bar, the page and the bottom navigation
 * one under another (none over the page), and a floating create button.
 */
export function Shell(props: ShellProps) {
  const {
    appName,
    appMark,
    homeHref = '/',
    nav,
    fab,
    searchPlaceholder,
    onSearch,
    actions,
    account,
    asidePanels = [],
    badge,
    scrollKey,
    stacked = false,
    bare = false,
    osShell = false,
    flush = false,
    side,
    lastApp,
    askApps,
    labels,
    children,
  } = props
  const Link = useLinkComponent()
  const os = useOs()
  const phone = useMediaQuery('(max-width: 47.99em)', false, { getInitialValueInEffect: false })
  const desktop = useMediaQuery('(min-width: 62em)', true, { getInitialValueInEffect: false })
  const [collapsed, setCollapsed] = useLocalStorage({ key: 'tnn.nav-collapsed', defaultValue: false })
  const [panelKey, setPanelKey] = useLocalStorage<string | null>({ key: 'tnn.side-panel', defaultValue: null })
  const [drawerOpened, drawer] = useDisclosure(false)
  // Thananan OS on a phone: the app's menu, and the apps to change to, each a sheet from the bottom
  const [menuOpened, menuSheet] = useDisclosure(false)
  const [appsOpened, appsSheet] = useDisclosure(false)
  // The apps by their marks (`osShell`): moving to one is a move in the one router, the one in front marked
  const appItems = nav.flatMap((s) => s.items).filter((i) => i.mark)
  // Tablet/phone: side panels open as a sheet on demand (never restored on load)
  const [sheetPanel, setSheetPanel] = useState<string | null>(null)

  useHotkeys([
    ['mod+K', onSearch],
    ['/', onSearch],
  ])

  const rail = !phone && (!desktop || collapsed)
  const navWidth = rail ? RAIL_WIDTH : NAV_WIDTH
  const openPanel = desktop ? asidePanels.find((p) => p.key === panelKey) : undefined
  const showAside = desktop && asidePanels.length > 0
  // A phone's bottom bar ends in the apps (`osShell`): back to the one last in front, or on an app's page (or before any
  // was opened) the sheet of apps to change to
  const appInFront = appItems.some((i) => i.active)
  const appBack = appItems.find((i) => i.key === lastApp)
  const appsItem: NavEntry | undefined =
    phone && osShell && (appItems.length > 0 || askApps)
      ? { key: 'apps', label: labels.apps ?? '', icon: IconLayoutGrid, href: appBack?.href ?? '', active: appInFront || askApps?.active }
      : undefined
  const changesApp = appInFront || !appBack
  // The one in front is marked by the menu (`active`, its `owns` too)
  const bottomItems = [...nav.flatMap((s) => s.items).filter((i) => i.bottom), ...(appsItem ? [appsItem] : [])]
  const floatingPanel = !desktop ? asidePanels.find((p) => p.key === sheetPanel) : undefined
  const floatingFab = phone && !bare && fab?.floats !== false ? fab : undefined

  // A phone's page scrolls in its own box: back on a page it is where it was left, a new one starts at the top
  const content = useRef<HTMLElement>(null)
  const [scrolled, setScrolled] = useState(false)
  useLayoutEffect(() => {
    if (stacked) return
    if (content.current) content.current.scrollTop = scrollKey ? (scrolledTo.get(scrollKey) ?? 0) : 0
  }, [scrollKey, stacked])
  const onScroll = (e: UIEvent<HTMLElement>) => {
    const top = e.currentTarget.scrollTop
    if (scrollKey) scrolledTo.set(scrollKey, top)
    setScrolled(top > 24)
  }
  const onPageScroll = (e: UIEvent<HTMLElement>) => setScrolled((e.target as HTMLElement).scrollTop > 24)

  const brand = (
    <UnstyledButton component={Link as 'a'} href={homeHref} className={classes.brand} aria-label={appName}>
      {appMark}
      <span className={classes.appName}>{appName}</span>
    </UnstyledButton>
  )

  const drawers = (
    <>
      <Drawer
        opened={drawerOpened}
        onClose={drawer.close}
        size={320}
        padding={0}
        withCloseButton={false}
        classNames={{ body: classes.drawerBody }}
      >
        <div className={classes.drawerHeader}>{brand}</div>
        {fab && !phone && <Fab fab={fab} rail={false} onNavigate={drawer.close} />}
        <NavList nav={nav} onNavigate={drawer.close} />
        {phone && asidePanels.length > 0 && (
          <NavList
            nav={[
              {
                key: 'panels',
                items: asidePanels.map((p) => ({ key: p.key, label: p.label, icon: p.icon, href: '#', count: p.badge })),
              },
            ]}
            onSelect={(key) => {
              drawer.close()
              setSheetPanel(key)
            }}
          />
        )}
      </Drawer>

      <Drawer
        opened={!!floatingPanel}
        onClose={() => setSheetPanel(null)}
        title={floatingPanel?.label}
        position={phone ? 'bottom' : 'right'}
        size={phone ? '85%' : PANEL_WIDTH + 24}
        radius={phone ? 'xl' : 0}
      >
        {floatingPanel?.content}
      </Drawer>
    </>
  )

  if (phone)
    return (
      <div className={classes.phone}>
        {!bare &&
          (osShell ? (
            // Where one is: in an app its name, ☰ opening its menu; else Thananan OS. Then search and the account
            <header className={classes.appBar}>
              {side ? (
                <UnstyledButton className={classes.appBarTitle} onClick={menuSheet.open} aria-label={`${labels.menu} ${side.title}`}>
                  <IconMenu2 size={22} stroke={1.75} />
                  {side.mark}
                  <span className={classes.appBarName}>{side.title}</span>
                </UnstyledButton>
              ) : (
                <div className={classes.appBarBrand}>
                  {appMark}
                  <span className={classes.appBarName}>{appName}</span>
                </div>
              )}
              <ActionIcon size={44} onClick={onSearch} aria-label={searchPlaceholder}>
                <IconSearch size={22} stroke={1.75} />
              </ActionIcon>
              {account}
            </header>
          ) : (
            <header className={classes.mobileHeader}>
              <div className={classes.mobileSearch}>
                <ActionIcon size={44} onClick={drawer.open} aria-label={labels.menu}>
                  <IconMenu2 size={24} stroke={1.75} />
                </ActionIcon>
                <UnstyledButton className={classes.searchText} onClick={onSearch}>
                  {searchPlaceholder}
                </UnstyledButton>
                {account}
              </div>
            </header>
          ))}

        <main
          ref={content}
          className={classes.phoneMain}
          data-with-fab={!!floatingFab || undefined}
          data-stacked={stacked || undefined}
          onScroll={stacked ? undefined : onScroll}
          onScrollCapture={stacked ? onPageScroll : undefined}
        >
          {children}
        </main>

        {bottomItems.length > 0 && !bare && (
          <nav className={classes.footer}>
            {bottomItems.map((item) => (
              <UnstyledButton
                key={item.key}
                {...(item === appsItem && changesApp
                  ? { component: 'button' as const, onClick: appsSheet.open, 'aria-haspopup': 'dialog' as const }
                  : { component: (item.external ? 'a' : Link) as 'a', href: item.href, 'data-menu': item.external ? undefined : '' })}
                className={classes.bottomItem}
                data-active={item.active || undefined}
                aria-current={item.active ? 'page' : undefined}
              >
                <span className={classes.bottomIcon}>
                  <item.icon size={24} stroke={1.75} />
                  <NavBadge item={item} />
                </span>
                {item.label}
              </UnstyledButton>
            ))}
          </nav>
        )}

        {floatingFab && <MobileFab fab={floatingFab} collapsed={scrolled} />}
        {drawers}
        {osShell && (
          <>
            <Drawer
              opened={menuOpened}
              onClose={menuSheet.close}
              title={side?.title}
              position="bottom"
              radius="xl"
              // As tall as what it holds (Mantine's bottom drawer is full height by default)
              styles={{ content: { height: 'auto', maxHeight: '92dvh' } }}
            >
              {side && <NavList nav={side.nav} onNavigate={menuSheet.close} />}
              {side?.footer && <div className={classes.sheetFooter}>{side.footer}</div>}
            </Drawer>
            <Drawer
              opened={appsOpened}
              onClose={appsSheet.close}
              title={labels.switchApp}
              position="bottom"
              radius="xl"
              // As tall as what it holds (Mantine's bottom drawer is full height by default)
              styles={{ content: { height: 'auto', maxHeight: '92dvh' } }}
            >
              <div className={classes.appGrid}>
                {appItems.map((item) => (
                  // Each app is a tab of its own, found as it was left; the one in front stays as it is
                  <UnstyledButton
                    key={item.key}
                    {...(item.active
                      ? { component: 'button' as const }
                      : { component: (item.external ? 'a' : Link) as 'a', href: item.href, 'data-menu': item.external ? undefined : '' })}
                    className={classes.appTile}
                    data-active={item.active || undefined}
                    aria-current={item.active ? 'page' : undefined}
                    onClick={appsSheet.close}
                  >
                    <NavIcon item={item} size={44} />
                    <span>{item.label}</span>
                  </UnstyledButton>
                ))}
              </div>
              {askApps && (
                <UnstyledButton component={Link as 'a'} href={askApps.href} className={classes.askApps} onClick={appsSheet.close}>
                  <IconPlus size={20} stroke={1.75} />
                  <span>{askApps.label}</span>
                  <IconChevronRight size={18} stroke={1.75} className={classes.askAppsArrow} />
                </UnstyledButton>
              )}
            </Drawer>
          </>
        )}
      </div>
    )

  if (osShell) {
    const tab = (item: NavEntry, menu: boolean) => (
      <UnstyledButton
        key={item.key}
        component={(item.external ? 'a' : Link) as 'a'}
        href={item.href}
        className={classes.topTab}
        data-menu={menu ? '' : undefined}
        data-active={item.active || undefined}
        aria-current={item.active ? 'page' : undefined}
      >
        <NavIcon item={item} size={item.mark ? 22 : 20} />
        <span>{item.label}</span>
        {item.count ? (
          <span className={classes.count} style={toneVars(countTone(item))}>
            {item.count}
          </span>
        ) : null}
      </UnstyledButton>
    )
    return (
      <div className={classes.desk}>
        <header className={classes.topBar}>
          <UnstyledButton component={Link as 'a'} href={homeHref} className={classes.topBrand} aria-label={appName}>
            {appMark}
          </UnstyledButton>
          <nav className={classes.topTabs} aria-label={appName}>
            {bottomItems.map((item) => tab(item, true))}
            {(appItems.length > 0 || askApps) && <span className={classes.topSep} />}
            {appItems.map((item) => tab(item, false))}
            {askApps && (
              <Tooltip label={askApps.label}>
                <ActionIcon
                  component={Link as 'a'}
                  href={askApps.href}
                  size={40}
                  radius="xl"
                  variant="subtle"
                  color="gray"
                  className={classes.topAsk}
                  data-active={askApps.active || undefined}
                  aria-current={askApps.active ? 'page' : undefined}
                  aria-label={askApps.label}
                >
                  <IconPlus size={20} stroke={1.75} />
                </ActionIcon>
              </Tooltip>
            )}
          </nav>
          <UnstyledButton className={`${classes.search} ${classes.topSearch}`} onClick={onSearch}>
            <IconSearch size={20} stroke={1.75} />
            <span className={classes.searchText}>{searchPlaceholder}</span>
            {desktop && <span className={classes.kbd}>{os === 'macos' ? '⌘K' : 'Ctrl K'}</span>}
          </UnstyledButton>
          {account}
        </header>
        <div className={classes.deskBody}>
          {side && (
            <aside className={classes.deskSide} data-folded={collapsed || undefined} aria-label={side.title}>
              <div className={classes.deskSideTitle}>
                {!collapsed && side.mark}
                {!collapsed && <span className={classes.deskSideName}>{side.title}</span>}
                <ActionIcon
                  size={36}
                  onClick={() => setCollapsed((c) => !c)}
                  aria-label={collapsed ? labels.expand : labels.collapse}
                  className={classes.deskFold}
                >
                  {collapsed ? (
                    <IconLayoutSidebarLeftExpand size={20} stroke={1.75} />
                  ) : (
                    <IconLayoutSidebarLeftCollapse size={20} stroke={1.75} />
                  )}
                </ActionIcon>
              </div>
              {side.fab && <Fab fab={side.fab} rail={collapsed} />}
              <ScrollArea className={classes.deskSideList} scrollbarSize={6}>
                {collapsed ? <RailItems nav={side.nav} /> : <NavList nav={side.nav} />}
              </ScrollArea>
              {!collapsed && side.footer && <div className={classes.deskSideFooter}>{side.footer}</div>}
            </aside>
          )}
          <main className={classes.deskMain}>{flush ? children : <div className={classes.sheet}>{children}</div>}</main>
        </div>
        {drawers}
      </div>
    )
  }

  return (
    <AppShell
      header={{ height: 64 }}
      navbar={{ width: navWidth, breakpoint: 'sm', collapsed: { mobile: true } }}
      aside={
        showAside
          ? { width: openPanel ? RAIL_WIDTH - 32 + PANEL_WIDTH : RAIL_WIDTH - 32, breakpoint: 'md', collapsed: { mobile: true } }
          : undefined
      }
      padding={0}
      transitionDuration={200}
      style={{ '--tnn-nav-width': `${navWidth}px` } as CSSProperties}
    >
      <AppShell.Header className={classes.header}>
        <div className={rail ? classes.brand : `${classes.brand} ${classes.brandWide}`}>
          <ActionIcon
            size={48}
            onClick={desktop ? () => setCollapsed((c) => !c) : drawer.open}
            aria-label={desktop ? (collapsed ? labels.expand : labels.collapse) : labels.menu}
          >
            <IconMenu2 size={24} stroke={1.75} />
          </ActionIcon>
          {brand}
          {badge}
        </div>
        <UnstyledButton className={classes.search} onClick={onSearch}>
          <IconSearch size={20} stroke={1.75} />
          <span className={classes.searchText}>{searchPlaceholder}</span>
          {desktop && <span className={classes.kbd}>{os === 'macos' ? '⌘K' : 'Ctrl K'}</span>}
        </UnstyledButton>
        <div className={classes.actions}>
          {!desktop &&
            asidePanels.map((p) => (
              <Tooltip key={p.key} label={p.label}>
                <ActionIcon size={40} onClick={() => setSheetPanel(p.key)} aria-label={p.label} className={classes.asideButton}>
                  <p.icon size={22} stroke={1.75} />
                  {p.badge ? <span className={classes.railBadge}>{p.badge}</span> : null}
                </ActionIcon>
              </Tooltip>
            ))}
          {actions}
          {account}
        </div>
      </AppShell.Header>

      <AppShell.Navbar className={classes.navbar}>
        <AppShell.Section grow component={ScrollArea} scrollbarSize={6}>
          {fab && <Fab fab={fab} rail={rail} />}
          {rail ? <RailItems nav={nav} /> : <NavList nav={nav} />}
        </AppShell.Section>
      </AppShell.Navbar>

      {showAside && (
        <AppShell.Aside className={classes.aside}>
          <div className={classes.asideRail}>
            {asidePanels.map((p) => (
              <Tooltip key={p.key} label={p.label} position="left">
                <ActionIcon
                  size={40}
                  className={classes.asideButton}
                  data-active={p.key === openPanel?.key || undefined}
                  onClick={() => setPanelKey((k) => (k === p.key ? null : p.key))}
                  aria-label={p.label}
                  aria-pressed={p.key === openPanel?.key}
                >
                  <p.icon size={22} stroke={1.75} />
                  {p.badge ? <span className={classes.railBadge}>{p.badge}</span> : null}
                </ActionIcon>
              </Tooltip>
            ))}
          </div>
          {openPanel && (
            <section className={classes.panel} aria-label={openPanel.label}>
              <header className={classes.panelHeader}>
                {openPanel.label}
                <CloseButton onClick={() => setPanelKey(null)} aria-label={labels.close} />
              </header>
              <ScrollArea style={{ flex: 1 }}>{openPanel.content}</ScrollArea>
            </section>
          )}
        </AppShell.Aside>
      )}

      <AppShell.Main className={classes.main}>
        <div className={classes.sheet}>{children}</div>
      </AppShell.Main>

      {drawers}
    </AppShell>
  )
}

/**
 * Back to the top of the page `from` is on, whatever it scrolls in: a phone's page box, a page's body on a computer,
 * or the window outside the shell
 */
export function scrollToTop(from: Element | null | undefined, behavior?: ScrollBehavior) {
  for (let box = from?.parentElement; box; box = box.parentElement) if (box.scrollTop > 0) box.scrollTo({ top: 0, behavior })
  window.scrollTo({ top: 0, behavior })
}

function Fab({ fab, rail, onNavigate }: { fab: ShellFab; rail: boolean; onNavigate?: () => void }) {
  const Link = useLinkComponent()
  const content = (
    <UnstyledButton
      component={(fab.href ? Link : 'button') as 'a'}
      href={fab.href}
      onClick={() => {
        fab.onClick?.()
        if (!fab.items) onNavigate?.()
      }}
      className={rail ? `${classes.fab} ${classes.fabRail}` : classes.fab}
      aria-label={fab.label}
    >
      <fab.icon size={24} stroke={1.75} />
      {!rail && fab.label}
    </UnstyledButton>
  )
  // A menu shows what it holds once open; a plain button on the rail says where it goes (a tooltip would take the menu's click)
  if (fab.items)
    return (
      <FabMenu fab={fab} position={rail ? 'right-start' : 'bottom-start'} onNavigate={onNavigate}>
        {content}
      </FabMenu>
    )
  return rail ? (
    <Tooltip label={fab.label} position="right">
      {content}
    </Tooltip>
  ) : (
    content
  )
}

/** `collapsed`: the page has scrolled, the button is a circle */
function MobileFab({ fab, collapsed }: { fab: ShellFab; collapsed: boolean }) {
  const Link = useLinkComponent()
  const button = (
    <UnstyledButton
      component={(fab.href ? Link : 'button') as 'a'}
      href={fab.href}
      onClick={fab.onClick}
      className={classes.mobileFab}
      data-collapsed={collapsed || undefined}
      aria-label={fab.label}
    >
      <fab.icon size={24} stroke={1.75} />
      <span className={classes.fabLabel}>{fab.label}</span>
    </UnstyledButton>
  )
  return fab.items ? (
    // The button stays put while the page scrolls under it, and so does its menu
    <FabMenu fab={fab} position="top-end" fixed>
      {button}
    </FabMenu>
  ) : (
    button
  )
}

/** What the create button makes, when it makes more than one thing */
function FabMenu({
  fab,
  position,
  fixed,
  onNavigate,
  children,
}: {
  fab: ShellFab
  position: FloatingPosition
  fixed?: boolean
  onNavigate?: () => void
  children: ReactNode
}) {
  const Link = useLinkComponent()
  return (
    <Menu
      position={position}
      radius="lg"
      offset={8}
      floatingStrategy={fixed ? 'fixed' : undefined}
      classNames={{ dropdown: classes.fabMenu, item: classes.fabItem }}
    >
      <Menu.Target>{children}</Menu.Target>
      <Menu.Dropdown>
        {fab.items?.map((item) => (
          <Menu.Item
            key={item.key}
            component={Link as 'a'}
            href={item.href}
            leftSection={<item.icon size={20} stroke={1.75} />}
            onClick={onNavigate}
          >
            {item.label}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  )
}

function NavList({ nav, onNavigate, onSelect }: { nav: NavSection[]; onNavigate?: () => void; onSelect?: (key: string) => void }) {
  const Link = useLinkComponent()
  return nav.map((section) => (
    <div key={section.key}>
      {section.label && <div className={classes.section}>{section.label}</div>}
      {section.items.map((item) => (
        <UnstyledButton
          key={item.key}
          component={(onSelect ? 'button' : item.external ? 'a' : Link) as 'a'}
          href={onSelect ? undefined : item.href}
          className={classes.item}
          data-menu={onSelect || item.external ? undefined : ''}
          data-active={item.active || undefined}
          aria-current={item.active ? 'page' : undefined}
          onClick={() => (onSelect ? onSelect(item.key) : onNavigate?.())}
        >
          <NavIcon item={item} size={20} />
          <span className={classes.itemLabel}>{item.label}</span>
          {item.parts?.length ? (
            <span className={classes.counts}>
              {item.parts.map((part) => (
                <Tooltip key={part.label} label={part.label} withArrow>
                  <span className={classes.count} style={toneVars(countTone(item, part))}>
                    {part.count}
                  </span>
                </Tooltip>
              ))}
            </span>
          ) : (
            item.count != null && (
              <span className={classes.count} style={toneVars(countTone(item))}>
                {item.count}
              </span>
            )
          )}
        </UnstyledButton>
      ))}
    </div>
  ))
}

function RailItems({ nav }: { nav: NavSection[] }) {
  const Link = useLinkComponent()
  return nav.map((section) =>
    section.items.map((item) => (
      <UnstyledButton
        key={item.key}
        component={(item.external ? 'a' : Link) as 'a'}
        href={item.href}
        className={classes.railItem}
        data-active={item.active || undefined}
        aria-current={item.active ? 'page' : undefined}
      >
        <span className={classes.railIcon}>
          <NavIcon item={item} size={22} />
          <NavBadge item={item} />
        </span>
        {item.label}
      </UnstyledButton>
    )),
  )
}

/** A menu number's colour: its kind's (the first one's over an icon), else the item's own, else something waiting */
const countTone = (item: NavEntry, part = item.parts?.[0]): Tone => part?.tone ?? item.countTone ?? 'warning'

/** The number over a menu icon (rail, bottom bar), in its colour */
function NavBadge({ item }: { item: NavEntry }) {
  return item.count ? (
    <span className={classes.railBadge} style={toneVars(countTone(item))}>
      {item.count}
    </span>
  ) : null
}

function NavIcon({ item, size }: { item: NavEntry; size: number }) {
  return item.mark ? item.mark(size) : <item.icon size={size} stroke={1.75} />
}
